// "use strict";
// (function () {
//   if (!customElements.get("partners-slider")) {
//     class PartnersSlider extends HTMLElement {
//       constructor() {
//         super();
//         this.slider = null;
//         this.swiper = null;
//       }
//       connectedCallback() {
//         this.setupElements();
//         this.initSwiper();
//       }
//       disconnectedCallback() {
//         if (this.swiper) {
//           this.swiper.destroy();
//         }
//       }
//       setupElements() {
//         this.grid = this.querySelector('[data-component="grid"]');
//         this.scrollbar = this.querySelector(".swiper-scrollbar");
//         this.btnNext = this.querySelector(".swiper-button-next");
//         this.btnPrev = this.querySelector(".swiper-button-prev");
//       }
//
//       getOriginalSlides() {
//         return Array.from(this.grid.querySelectorAll(".card-partners"));
//       }
//
//       initSwiper() {
//         if (!this.slider) return;
//
//         let { slidesPerView, slidesPerViewTb, slidesPerViewMb, sliderLength } =
//           this.dataset;
//
//         sliderLength = +sliderLength;
//         slidesPerView =
//           slidesPerView > sliderLength ? sliderLength : +slidesPerView;
//         slidesPerViewTb =
//           slidesPerViewTb > sliderLength ? sliderLength : +slidesPerViewTb;
//         slidesPerViewMb =
//           slidesPerViewMb > sliderLength ? sliderLength : +slidesPerViewMb;
//
//         this.swiper = new Swiper(this.slider, {
//           slidesPerView: slidesPerViewMb,
//           spaceBetween: 16,
//           grabCursor: true,
//           navigation: {
//             nextEl: this.btnNext,
//             prevEl: this.btnPrev,
//           },
//           scrollbar: {
//             el: this.scrollbar,
//             draggable: true,
//           },
//         });
//       }
//     }
//     customElements.define("partners-slider", PartnersSlider);
//   }
// })();
//

"use strict";
(function () {
  if (!customElements.get("partners-slider")) {
    /**
     * PartnersSlider - A responsive custom element that converts to a mobile slider
     * only on screens smaller than 750px. Intelligently manages Swiper initialization
     * and cleanup based on viewport changes.
     *
     * Features:
     * - Responsive initialization (mobile only: < 750px)
     * - Automatic swiper structure creation
     * - Listens to resize and orientationchange events
     * - Proper cleanup when transitioning to desktop
     * - Memory-leak prevention through careful DOM manipulation
     *
     * @class PartnersSlider
     * @extends HTMLElement
     */
    class PartnersSlider extends HTMLElement {
      constructor() {
        super();
        /** @type {HTMLElement|null} The grid container with all cards */
        this.grid = null;
        /** @type {Swiper|null} Swiper instance (null if not initialized) */
        this.swiper = null;
        /** @type {HTMLElement|null} Swiper wrapper element */
        this.swiperWrapper = null;
        /** @type {HTMLElement|null} Outer swiper container */
        this.swiperContainer = null;
        /** @type {HTMLElement|null} Scrollbar element for mobile */
        this.scrollbar = null;
        /** @type {HTMLElement|null} Next navigation button */
        this.btnNext = null;
        /** @type {HTMLElement|null} Previous navigation button */
        this.btnPrev = null;
        /** @type {boolean} Tracks whether we're currently in mobile mode */
        this.isMobileMode = false;
        /** @type {number} Mobile breakpoint in pixels */
        this.MOBILE_BREAKPOINT = 750;
        /** @type {Function|null} Bound resize handler for cleanup in disconnectedCallback */
        this.resizeHandler = null;
      }

      /**
       * Lifecycle hook called when element is inserted into DOM.
       * Sets up all elements, checks viewport, and initializes swiper if mobile.
       * @returns {void}
       */
      connectedCallback() {
        this.setupElements();
        this.setupEventListeners();
        this.checkAndInitSlider();
      }

      /**
       * Lifecycle hook called when element is removed from DOM.
       * Properly cleans up Swiper instance and removes event listeners.
       * @returns {void}
       */
      disconnectedCallback() {
        // Remove event listeners to prevent memory leaks
        if (this.resizeHandler) {
          window.removeEventListener("resize", this.resizeHandler);
          window.removeEventListener("orientationchange", this.resizeHandler);
        }

        // Destroy swiper instance if it exists
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }
      }

      /**
       * Sets up references to all DOM elements needed for slider functionality.
       * Queries for the grid container, scrollbar, and navigation buttons.
       * @returns {void}
       */
      setupElements() {
        // Main grid container holding all card-partners elements
        this.grid = this.querySelector('[data-component="grid"]');

        // Optional scrollbar for mobile navigation feedback
        this.scrollbar = this.querySelector(".swiper-scrollbar");

        // Navigation buttons for swiper control
        this.btnNext = this.querySelector(".swiper-button-next");
        this.btnPrev = this.querySelector(".swiper-button-prev");

        if (!this.grid) {
          console.warn(
            "PartnersSlider: Missing grid container with [data-component='grid']",
          );
        }

        if (!this.btnNext || !this.btnPrev) {
          console.warn("PartnersSlider: Missing navigation buttons");
        }
      }

      /**
       * Sets up event listeners for responsive behavior.
       * Binds resize and orientationchange events to check viewport changes.
       * @returns {void}
       */
      setupEventListeners() {
        // Use a throttled resize handler to avoid performance issues
        this.resizeHandler = this.throttle(() => {
          this.checkAndInitSlider();
        }, 25);

        // Listen for window resize events
        window.addEventListener("resize", this.resizeHandler);

        // Listen for device orientation changes (important for mobile)
        window.addEventListener("orientationchange", this.resizeHandler);
      }

      /**
       * Throttle function to limit how often a callback is executed.
       * Prevents excessive calls during continuous resize events.
       *
       * @param {Function} callback - Function to throttle
       * @param {number} delay - Minimum milliseconds between calls
       * @returns {Function} Throttled function
       */
      throttle(callback, delay) {
        let lastCall = 0;
        let timeoutId = null;

        return function () {
          const now = Date.now();
          const timeSinceLastCall = now - lastCall;

          if (timeSinceLastCall >= delay) {
            lastCall = now;
            callback();
          } else {
            // Schedule callback for later if we're within throttle window
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              lastCall = Date.now();
              callback();
            }, delay - timeSinceLastCall);
          }
        };
      }

      /**
       * Checks current viewport width and initializes or destroys slider accordingly.
       * Only initializes Swiper on mobile (< 750px).
       * Destroys Swiper when transitioning to desktop (>= 750px).
       *
       * @returns {void}
       */
      checkAndInitSlider() {
        const isMobile = window.innerWidth < this.MOBILE_BREAKPOINT;

        // If viewport hasn't changed modes, do nothing
        if (isMobile === this.isMobileMode) {
          return;
        }

        // Transitioning from desktop to mobile
        if (isMobile) {
          this.initSwiper();
          this.isMobileMode = true;
        }
        // Transitioning from mobile to desktop
        else {
          this.destroySlider();
          this.isMobileMode = false;
        }
      }

      /**
       * Retrieves all card elements from the grid.
       * @returns {Array<HTMLElement>} Array of card-partners elements
       */
      getCards() {
        return Array.from(this.grid.querySelectorAll(".card-partners-wrapper"));
      }

      /**
       * Creates the swiper DOM structure required by the Swiper library.
       * Wraps the grid in <div class="swiper"><div class="swiper-wrapper">
       * and adds swiper-slide class to each card.
       *
       * Structure created:
       * ```
       * <div class="swiper" data-created="true">
       *   <div class="swiper-wrapper" data-created="true">
       *     [cards with swiper-slide class]
       *   </div>
       * </div>
       * ```
       *
       * The data-created attribute helps identify elements we added for cleanup.
       *
       * @returns {void}
       */
      createSwiperStructure() {
        const cards = this.getCards();

        // Create wrapper element that Swiper requires
        this.swiperWrapper = document.createElement("div");
        this.swiperWrapper.className = "swiper-wrapper";
        this.swiperWrapper.setAttribute("data-created", "true");

        // Create outer swiper container
        this.swiperContainer = document.createElement("div");
        this.swiperContainer.className = "swiper";
        this.swiperContainer.setAttribute("data-created", "true");

        // Move all cards into the wrapper
        cards.forEach((card) => {
          // Add swiper-slide class for Swiper functionality
          card.classList.add("swiper-slide");
          this.swiperWrapper.appendChild(card);
        });

        // Append wrapper to container, then container to grid
        this.swiperContainer.appendChild(this.swiperWrapper);
        this.grid.appendChild(this.swiperContainer);
      }

      /**
       * Initializes the Swiper instance for mobile viewing.
       * Only called when viewport is < 750px.
       *
       * Configuration:
       * - slidesPerView: 1 (single card per view on mobile)
       * - spaceBetween: 16px gap between cards
       * - grabCursor: true for better UX
       * - Navigation buttons for prev/next
       * - Draggable scrollbar for progress feedback
       *
       * @returns {void}
       */
      initSwiper() {
        // Early exit if elements aren't ready
        if (!this.grid || !this.btnNext || !this.btnPrev) {
          console.warn("PartnersSlider: Missing required elements for Swiper");
          return;
        }

        // Create the proper DOM structure for Swiper
        this.createSwiperStructure();

        // ========== INITIALIZE SWIPER INSTANCE ==========
        this.swiper = new Swiper(this.swiperContainer, {
          slidesPerView: 1,
          spaceBetween: 16,
          grabCursor: true,

          // Navigation setup
          navigation: {
            nextEl: this.btnNext,
            prevEl: this.btnPrev,
          },

          // Optional scrollbar for visual feedback
          scrollbar: {
            el: this.scrollbar,
            draggable: true,
          },
        });
      }

      /**
       * Destroys the Swiper instance and reverts DOM to original state.
       * Removes swiper-slide classes and unwraps cards from swiper containers.
       *
       * Cleanup process:
       * 1. Destroy Swiper instance
       * 2. Remove swiper-slide class from all cards
       * 3. Move cards back to grid (unwrap from swiper structure)
       * 4. Remove swiper container and wrapper elements we created
       *
       * @returns {void}
       */
      destroySlider() {
        // Destroy Swiper instance
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }

        // Get all cards currently in the swiper wrapper
        const cards = this.getCards();

        // Remove swiper-slide class from all cards
        cards.forEach((card) => {
          card.classList.remove("swiper-slide");
        });

        // Move cards back to the grid (unwrap from swiper structure)
        cards.forEach((card) => {
          this.grid.appendChild(card);
        });

        // Remove swiper container and wrapper elements we created
        if (this.swiperContainer) {
          this.swiperContainer.remove();
          this.swiperContainer = null;
        }

        if (this.swiperWrapper) {
          this.swiperWrapper = null;
        }
      }
    }

    // Register the custom element
    customElements.define("partners-slider", PartnersSlider);
  }
})();
